/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ID, MetedataLinkDto, ProduceStatusDto } from '@gh/common-shared-data';
import { AbstractRestService, arrayOf, identity, RestSettings, SlicedDataSource } from '@gh/core-data';
import { coalesce } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  CommunicationAttachmentDto,
  JobChangeOrderDetailsPreviewDto,
  JobChangeOrderGeneratedDocumentSetDto,
  JobChangeOrderPrintRequestDto,
  JobChangeOrderProduceRequestDto,
  JobChangeOrderProduceResponseDto,
  JobChangeOrderReplaceOrdersRequestDto,
  JobListFilterDto,
  JobListFilterMapper,
  JobListItemDto,
  JobListItemMapper,
} from '../../index';
import { LetterDto, LetterMapper } from '../../letters';
import {
  JobChangeOrderDetailsDto,
  JobChangeOrderDetailsSaveDto,
  JobChangeOrderDetailsSaveResponseDto,
  JobChangeOrderElementIdsGetResponseDto,
  JobChangeOrderListDto,
  JobChangeOrderTabListDto,
} from './job-change-order.dto';
import {
  JobChangeOrderDetailsMapper,
  JobChangeOrderDetailsPreviewMapper,
  JobChangeOrderDetailsSaveMapper,
  JobChangeOrderDetailsSaveResponseMapper,
  JobChangeOrderListMapper,
  JobChangeOrderTabListMapper,
} from './job-change-order.mapper';
import { ENV_BASE_URL } from '@gh/config';

@Injectable()
export class JobChangeOrderRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  findAll(jobId: ID): Observable<JobChangeOrderListDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders`, JobChangeOrderListMapper);
  }

  findAllForTab(jobId: ID): Observable<JobChangeOrderTabListDto> {
    return this.httpGet(
      `${this.bp}/jobs/${jobId}/change-orders/tab`,
      JobChangeOrderTabListMapper,
    );
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItemDto> {
    return this.createSlicedDataSourceGet(`${this.jad}/job-change-orders`,
                                          filter, JobListFilterMapper, arrayOf(JobListItemMapper));
  }

  init(jobId: ID): Observable<JobChangeOrderDetailsDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders/init`, JobChangeOrderDetailsMapper);
  }

  clone(jobId: ID, changeOrderId: ID): Observable<JobChangeOrderDetailsDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/clone`, JobChangeOrderDetailsMapper);
  }

  get(jobId: ID, changeOrderId: ID): Observable<JobChangeOrderDetailsDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}`, JobChangeOrderDetailsMapper);
  }

  create(jobId: ID, changeOrder: JobChangeOrderDetailsSaveDto): Observable<JobChangeOrderDetailsSaveResponseDto> {
    return this.httpPost(
      `${this.bp}/jobs/${jobId}/change-orders`,
      changeOrder,
      JobChangeOrderDetailsSaveMapper,
      JobChangeOrderDetailsSaveResponseMapper);
  }

  update(jobId: ID,
         changeOrderId: ID,
         changeOrder: JobChangeOrderDetailsSaveDto): Observable<JobChangeOrderDetailsSaveResponseDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/change-orders/recalculate`,
      changeOrder,
      JobChangeOrderDetailsSaveMapper,
      JobChangeOrderDetailsSaveResponseMapper);
  }

  recalculate(jobId: ID,
              changeOrderId: ID,
              changeOrder: JobChangeOrderDetailsSaveDto): Observable<JobChangeOrderDetailsSaveResponseDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/change-orders/recalculate`,
      changeOrder,
      JobChangeOrderDetailsSaveMapper,
      JobChangeOrderDetailsSaveResponseMapper);
  }

  generateChangeOrderIds(jobId: ID,
                         itemsAmount: number,
                         componentsAmount: number): Observable<JobChangeOrderElementIdsGetResponseDto> {
    return this.httpGet(
      `${this.bp}/jobs/${jobId}/change-orders/variations-ids`,
      { itemsAmount, componentsAmount },
      identity(),
      identity());
  }

  confirmProduce(jobId: ID, changeOrderId: ID, request: LetterDto): Observable<LetterDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/confirm`,
      request,
      LetterMapper);
  }

  getProduceStatus(jobId: ID, changeOrderId: ID): Observable<ProduceStatusDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/produce-status`, identity());
  }

  generateDocuments(jobId: ID, changeOrderId: ID): Observable<JobChangeOrderGeneratedDocumentSetDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/docs`, identity());
  }

  printDocuments(jobId: ID, changeOrderId: ID, request: JobChangeOrderPrintRequestDto): Observable<any> {
    return this.httpPut(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/print`, request, identity(), identity());
  }

  getS3Link(jobId: ID): Observable<MetedataLinkDto> {
    return this.httpGet(`${this.bp}/jobs/${jobId}/change-orders/complete-metadata`, identity());
  }

  previewDocument(jobId: ID, changeOrderId: ID, changeOrder: JobChangeOrderDetailsPreviewDto): Observable<LetterDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/preview`,
      changeOrder,
      JobChangeOrderDetailsPreviewMapper,
      LetterMapper);
  }

  produceChangeOrderDoc(jobId: ID,
                        changeOrderId: ID,
                        changeOrder: JobChangeOrderDetailsPreviewDto): Observable<CommunicationAttachmentDto> {
    return this.httpPut(
      `${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/produce-change-order-doc`,
      changeOrder,
      JobChangeOrderDetailsPreviewMapper,
      identity());
  }

  produce(jobId: ID,
          changeOrderId: ID,
          request: JobChangeOrderProduceRequestDto): Observable<JobChangeOrderProduceResponseDto> {
    return this.httpPut(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/produce`, request, identity(), identity()).pipe(
      map$((response: JobChangeOrderProduceResponseDto) => coalesce(response, { affectedCostCentersCount: 0 })));
  }

  replaceOrders(jobId: ID,
                changeOrderId: ID,
                request: JobChangeOrderReplaceOrdersRequestDto): Observable<JobChangeOrderProduceResponseDto> {
    return this.httpPut(`${this.bp}/jobs/${jobId}/change-orders/${changeOrderId}/replace-orders`,
                        request,
                        identity(),
                        identity()).pipe(
      map$((response: JobChangeOrderProduceResponseDto) => coalesce(response, { affectedCostCentersCount: 0 })));
  }
}
